import './FileUpload.scss';

import { useCallback, useMemo, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { faCloudArrowUp, faFilePdf, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@myome/ui';

const MAX_PDF_UPLOAD_BYTES = 25 * 1024 * 1024;

interface FileUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  // External error from the form layer (e.g. "required" on submit).
  // Internal rejections (wrong MIME, oversize) take precedence over this.
  externalError?: string;
  disabled?: boolean;
}

const ACCEPTED_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
};

const messageForRejection = (rejection: FileRejection | undefined): string | undefined => {
  if (!rejection) return undefined;
  const code = rejection.errors[0]?.code;
  if (code === 'file-too-large') {
    return 'That file is over 25 MB. Please upload a smaller PDF.';
  }
  if (code === 'file-invalid-type') {
    return 'Only PDF files are accepted.';
  }
  return rejection.errors[0]?.message ?? 'Could not accept that file.';
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileUpload = ({ value, onChange, externalError, disabled = false }: FileUploadProps) => {
  const [internalError, setInternalError] = useState<string | undefined>();

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      const rejectionMessage = messageForRejection(rejections[0]);
      if (rejectionMessage) {
        setInternalError(rejectionMessage);
        return;
      }
      setInternalError(undefined);
      onChange(accepted[0] ?? null);
    },
    [onChange],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_PDF_UPLOAD_BYTES,
    multiple: false,
    disabled,
    noClick: true,
    noKeyboard: true,
  });

  const handleRemove = useCallback(() => {
    setInternalError(undefined);
    onChange(null);
  }, [onChange]);

  const displayError = internalError ?? externalError;
  const rootClassName = useMemo(() => {
    const classes = ['file-upload__dropzone'];
    if (isDragActive) classes.push('is-active');
    if (displayError) classes.push('is-error');
    if (value) classes.push('is-filled');
    if (disabled) classes.push('is-disabled');
    return classes.join(' ');
  }, [isDragActive, displayError, value, disabled]);

  if (value) {
    return (
      <div id='report_pdf' className='file-upload'>
        <div className='file-upload__filled'>
          <div className='file-upload__filled-icon' aria-hidden='true'>
            <FontAwesomeIcon icon={faFilePdf} />
          </div>
          {/* Live region is scoped to the meta block (filename + size + valid status)
              so it's announced when a file is added/replaced without re-announcing the
              interactive Remove button below. */}
          <div className='file-upload__filled-meta' role='status' aria-live='polite'>
            <div className='file-upload__filename' title={value.name} data-openreplay-sanitize>
              {value.name}
            </div>
            <div className='file-upload__filesize'>
              {formatBytes(value.size)} · <span className='file-upload__valid'>Looks valid</span>
            </div>
          </div>
          <button
            type='button'
            className='file-upload__remove'
            onClick={handleRemove}
            disabled={disabled}
            aria-label='Remove file'
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {displayError && (
          <div className='file-upload__error' role='alert'>
            {displayError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div id='report_pdf' className='file-upload'>
      <div {...getRootProps({ className: rootClassName })}>
        <input {...getInputProps()} />
        <FontAwesomeIcon icon={faCloudArrowUp} className='file-upload__icon' aria-hidden='true' />
        <div className='file-upload__title'>
          <span className='req' aria-hidden>
            *{' '}
          </span>
          Drop the patient&apos;s report PDF here
        </div>
        <div className='file-upload__separator'>or</div>
        <Button
          variant='secondary'
          onClick={open}
          disabled={disabled}
          type='button'
          ariaLabel='Browse files'
          dataTestId='file-upload-browse'
        >
          Browse files
        </Button>
        <div className='file-upload__constraints'>PDF only · single file · up to 25 MB</div>
      </div>
      {displayError && (
        <div className='file-upload__error' role='alert'>
          {displayError}
        </div>
      )}
    </div>
  );
};
